import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CORPORATE,
  CANDIDATE_CHIP_SELECTED,
  CANDIDATE_CHIP_SELECTED_REMOVE_HOVER
} from '../constants/candidateFormStyles';
import { MODAL_FILTER_MENU_Z_INDEX } from '../constants/filterBar';

const CHIP_THEME = {
  chip: CANDIDATE_CHIP_SELECTED,
  chipRemove: CANDIDATE_CHIP_SELECTED_REMOVE_HOVER,
  badge: CANDIDATE_CHIP_SELECTED
};

const VARIANTS = {
  skill: {
    ...CHIP_THEME,
    empty: 'No skills added yet',
    createPrefix: 'Add skill'
  },
  location: {
    ...CHIP_THEME,
    empty: 'No locations added yet',
    createPrefix: 'Add location'
  },
  expertise: {
    ...CHIP_THEME,
    empty: 'No areas selected yet',
    createPrefix: 'Add area'
  }
};

/**
 * Advanced multi-value picker (skills, locations): search, catalog, create-new, portaled menu.
 */
export default function TagInputPicker({
  selected = [],
  onChange,
  catalog = [],
  onCreateItem,
  variant = 'skill',
  label = 'Search',
  placeholder = 'Type to search or add…',
  hint,
  error,
  allowRemove = true,
  showCountBadge = true,
  menuZIndex = MODAL_FILTER_MENU_Z_INDEX,
  normalizeItem = item => String(item || '').trim(),
  searchHaystackForItem = null
}) {
  const theme = VARIANTS[variant] || VARIANTS.skill;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 280 });
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const selectedNorm = useMemo(
    () => selected.map(s => normalizeItem(s)).filter(Boolean),
    [selected, normalizeItem]
  );

  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog
      .map(normalizeItem)
      .filter(Boolean)
      .filter(item => !selectedNorm.some(s => s.toLowerCase() === item.toLowerCase()))
      .filter(item => {
        if (!q) return true;
        const hay = searchHaystackForItem ? searchHaystackForItem(item) : item;
        return String(hay || item).toLowerCase().includes(q);
      })
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [catalog, selectedNorm, query, normalizeItem, searchHaystackForItem]);

  const trimmedQuery = query.trim();
  const qLower = trimmedQuery.toLowerCase();
  const exactInCatalog = catalog.some(s => normalizeItem(s).toLowerCase() === qLower);
  const exactSelected = selectedNorm.some(s => s.toLowerCase() === qLower);
  const canCreate = trimmedQuery && !exactInCatalog && !exactSelected;
  const showMenu = open && (available.length > 0 || canCreate);

  const updatePosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuH = Math.min(menuRef.current?.offsetHeight || 240, 240);
    const gap = 6;
    const pad = 8;
    let top = rect.bottom + gap;
    if (top + menuH > window.innerHeight - pad) {
      const above = rect.top - menuH - gap;
      if (above >= pad) top = above;
    }
    let left = rect.left;
    const width = Math.max(rect.width, 280);
    if (left + width > window.innerWidth - pad) left = Math.max(pad, window.innerWidth - width - pad);
    setPos({ top, left, width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = e => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    const onPointerDown = e => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, updatePosition, available.length, canCreate]);

  const addItem = async raw => {
    const item = normalizeItem(raw);
    if (!item || selectedNorm.some(s => s.toLowerCase() === item.toLowerCase())) return;
    if (!catalog.some(c => normalizeItem(c).toLowerCase() === item.toLowerCase()) && onCreateItem) {
      await onCreateItem(item);
    }
    onChange([...selectedNorm, item]);
    setQuery('');
    setOpen(false);
  };

  const removeItem = item => {
    onChange(selectedNorm.filter(s => s !== item));
  };

  const menu =
    showMenu &&
    createPortal(
      <ul
        ref={menuRef}
        role="listbox"
        className={`fixed ${CORPORATE.menu} max-h-60 overflow-y-auto`}
        style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: menuZIndex }}
        onMouseDown={e => e.preventDefault()}
      >
        {available.length === 0 && !canCreate && (
          <li className="px-3 py-3 text-xs text-[hsl(var(--ds-muted-fg))]">No matches</li>
        )}
        {available.map(item => (
          <li key={item}>
            <button
              type="button"
              role="option"
              className={CORPORATE.menuItem}
              onClick={() => addItem(item)}
            >
              {item}
            </button>
          </li>
        ))}
        {canCreate && (
          <li>
            <button type="button" className={CORPORATE.menuItemCreate} onClick={() => addItem(trimmedQuery)}>
              + {theme.createPrefix} &quot;{trimmedQuery}&quot;
            </button>
          </li>
        )}
      </ul>,
      document.body
    );

  return (
    <div className="space-y-3 relative" ref={rootRef}>
      {showCountBadge && selectedNorm.length > 0 && (
        <span
          className={`absolute -top-1 right-0 z-10 ${CORPORATE.badge} ${theme.badge}`}
          aria-label={`${selectedNorm.length} selected`}
        >
          {selectedNorm.length}
        </span>
      )}

      {error && <p className="text-xs text-red-600 font-medium">{error}</p>}

      {selectedNorm.length > 0 && (
        <div className="flex flex-wrap gap-2 pr-12" role="list" aria-label="Selected items">
          {selectedNorm.map(item => (
            <span key={item} className={`${CORPORATE.chip} ${theme.chip}`} role="listitem">
              {item}
              {allowRemove && (
                <button
                  type="button"
                  className={`${CORPORATE.chipRemove} ${theme.chipRemove}`}
                  onClick={() => removeItem(item)}
                  aria-label={`Remove ${item}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        {label && <span className={CORPORATE.label}>{label}</span>}
        <div className="relative flex">
          <input
            ref={inputRef}
            type="text"
            className={CORPORATE.searchInput}
            placeholder={placeholder}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                addItem(trimmedQuery);
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setOpen(true);
              }
            }}
            autoComplete="off"
            aria-expanded={showMenu}
            aria-haspopup="listbox"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-xs"
            onClick={() => {
              setOpen(o => !o);
              if (!open) inputRef.current?.focus();
            }}
            aria-label="Toggle list"
          >
            {open ? '▴' : '▾'}
          </button>
        </div>
      </div>

      {hint && <p className="text-xs text-[hsl(var(--ds-muted-fg))] leading-relaxed">{hint}</p>}
      {menu}
    </div>
  );
}
